import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import Shell from "@/components/Shell";
import { Plus, ArrowRight, Users, FileText, Sparkles, ChevronRight } from "lucide-react";

const CLIENT_TINTS = ["tint-blue-soft", "tint-matcha-soft", "tint-lavender-soft", "tint-yellow-soft", "tint-pink-soft"];
const CLIENT_DOTS  = ["var(--blue-deep)", "var(--matcha-deep)", "var(--lavender-deep)", "var(--yellow-deep)", "var(--pink-deep)"];

const TYPE_DOTS = {
  video: "var(--blue-deep)",
  email: "var(--matcha-deep)",
  social: "var(--yellow-deep)",
  sales: "var(--pink-deep)",
};

function greet() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

export default function HomePage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [clients, setClients] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [stats, setStats] = useState({ clients: 0, scripts: 0, samples: 0 });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");

  const load = useCallback(async () => {
    try {
      const [c, s, st] = await Promise.all([
        api.get("/clients"),
        api.get("/scripts?limit=8"),
        api.get("/stats"),
      ]);
      setClients(c.data);
      setScripts(s.data);
      setStats(st.data);
    } catch { toast.error("Failed to load dashboard"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createClient = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      const { data } = await api.post("/clients", { name: newName, description: newDesc });
      setClients((c) => [data, ...c]);
      setStats((st) => ({ ...st, clients: st.clients + 1 }));
      setNewName(""); setNewDesc(""); setCreating(false);
      toast.success("Client added");
      nav(`/clients/${data.id}`);
    } catch { toast.error("Failed to create client"); }
  };

  return (
    <Shell>
      <div className="max-w-6xl mx-auto">
        {/* Greeting */}
        <div className="mb-10">
          <div className="mono-label mb-2">Studio</div>
          <h1 className="font-serif-display text-5xl md:text-6xl tracking-tight leading-none" data-testid="home-greeting">
            {greet()}, <span style={{ color: "var(--primary)" }}>{user?.name?.split(" ")[0] || "friend"}</span>.
          </h1>
          <p className="mt-3 text-base max-w-xl" style={{ color: "var(--ink-2)" }}>
            Pick a client to dive back into their voice, or spin up a new script from scratch.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 md:gap-6 mb-14" data-testid="stats-row">
          <StatCard tint="tint-blue-soft" icon={<Users className="w-4 h-4" />} label="Clients" value={stats.clients} testid="stat-clients" />
          <StatCard tint="tint-matcha-soft" icon={<FileText className="w-4 h-4" />} label="Samples fed" value={stats.samples} testid="stat-samples" />
          <StatCard tint="tint-lavender-soft" icon={<Sparkles className="w-4 h-4" />} label="Scripts written" value={stats.scripts} testid="stat-scripts" />
        </div>

        {/* Clients */}
        <section className="mb-14">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="mono-label mb-1">Your clients</div>
              <h2 className="font-serif-display text-3xl tracking-tight">Whose voice today?</h2>
            </div>
            <button
              data-testid="new-client-toggle"
              onClick={() => setCreating((v) => !v)}
              className="btn-primary text-sm px-4 py-2 rounded-md inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> New client
            </button>
          </div>

          {creating && (
            <form onSubmit={createClient} className="mb-6 tint-lavender-soft hair rounded-md p-6 grid md:grid-cols-[1fr_1fr_auto] gap-3 items-end rise-in" data-testid="new-client-form">
              <label className="block">
                <span className="mono-label block mb-2">Name</span>
                <input
                  data-testid="new-client-name" autoFocus required
                  placeholder="Isla Whitmore"
                  value={newName} onChange={(e) => setNewName(e.target.value)}
                  className="w-full hair px-3 py-2 rounded-md text-sm focus-primary"
                  style={{ background: "var(--paper)" }}
                />
              </label>
              <label className="block">
                <span className="mono-label block mb-2">Short note (optional)</span>
                <input
                  data-testid="new-client-desc"
                  placeholder="Sleep coach · Instagram-first"
                  value={newDesc} onChange={(e) => setNewDesc(e.target.value)}
                  className="w-full hair px-3 py-2 rounded-md text-sm focus-primary"
                  style={{ background: "var(--paper)" }}
                />
              </label>
              <div className="flex gap-2">
                <button data-testid="new-client-submit" type="submit" className="btn-primary px-4 py-2 text-sm rounded-md">Add</button>
                <button type="button" onClick={() => setCreating(false)} className="btn-ghost px-3 py-2 text-sm rounded-md">Cancel</button>
              </div>
            </form>
          )}

          {(() => {
            if (loading) return <div className="mono-label pulse-soft">Loading</div>;
            if (clients.length === 0) return (
              <div className="hair rounded-md p-10 text-center" style={{ background: "var(--paper)" }}>
                <div className="font-serif-display text-3xl mb-2">No clients yet</div>
                <p className="text-sm mb-6" style={{ color: "var(--ink-2)" }}>
                  Add your first client and feed them a few writing samples.
                </p>
                <button
                  data-testid="empty-add-client"
                  onClick={() => setCreating(true)}
                  className="btn-primary px-5 py-2.5 rounded-md inline-flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" /> Add a client
                </button>
              </div>
            );
            return (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="clients-grid">
                {clients.map((c, i) => (
                  <ClientCard key={c.id} client={c} tintIndex={i % CLIENT_TINTS.length} onOpen={() => nav(`/clients/${c.id}`)} />
                ))}
              </div>
            );
          })()}
        </section>

        {/* Recent scripts */}
        {scripts.length > 0 && (
          <section>
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="mono-label mb-1">Recent</div>
                <h2 className="font-serif-display text-3xl tracking-tight">Latest scripts</h2>
              </div>
            </div>
            <ul className="space-y-2" data-testid="recent-scripts">
              {scripts.map((s, i) => (
                <li key={s.id} className="rise-in" style={{ animationDelay: `${i * 30}ms` }}>
                  <button
                    onClick={() => nav(`/clients/${s.client_id}`)}
                    data-testid={`recent-script-${s.id}`}
                    className="w-full text-left hair rounded-md px-5 py-4 flex items-center justify-between hover:bg-[var(--surface-hover)] transition-colors duration-200"
                    style={{ background: "var(--paper)" }}
                  >
                    <div className="min-w-0 flex items-center gap-3">
                      <span className="dot" style={{ background: TYPE_DOTS[s.script_type] }} />
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{s.topic}</div>
                        <div className="mono-label mt-1 truncate">
                          {s.client_name} · {s.script_type} · {new Date(s.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 shrink-0" style={{ color: "var(--ink-2)" }} />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </Shell>
  );
}

function StatCard({ tint, icon, label, value, testid }) {
  return (
    <div className={`${tint} hair rounded-md p-5 md:p-6`} data-testid={testid}>
      <div className="mono-label flex items-center gap-2">{icon} {label}</div>
      <div className="font-serif-display text-4xl md:text-5xl mt-2 tracking-tight">{value}</div>
    </div>
  );
}

function ClientCard({ client, tintIndex, onOpen }) {
  return (
    <button
      onClick={onOpen}
      data-testid={`client-card-${client.id}`}
      className={`${CLIENT_TINTS[tintIndex]} hair rounded-md p-6 text-left transition-transform duration-200 hover:-translate-y-0.5 rise-in`}
      style={{ animationDelay: `${tintIndex * 40}ms` }}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="dot" style={{ background: CLIENT_DOTS[tintIndex] }} />
        <ArrowRight className="w-4 h-4" style={{ color: "var(--ink-2)" }} />
      </div>
      <div className="font-serif-display text-2xl tracking-tight leading-tight" data-testid={`client-name-${client.id}`}>
        {client.name}
      </div>
      {client.description && (
        <p className="text-sm mt-2 line-clamp-2" style={{ color: "var(--ink-2)" }}>{client.description}</p>
      )}
      <div className="mono-label mt-4 flex items-center gap-4">
        <span>{client.sample_count} sample{client.sample_count !== 1 ? "s" : ""}</span>
        <span>·</span>
        <span>{client.scripts_count} script{client.scripts_count !== 1 ? "s" : ""}</span>
      </div>
    </button>
  );
}
